import { useState, useRef, useCallback, useEffect } from 'react';
import { VoiceActivityDetector, VAD_STATES } from './voiceActivityDetector';
import { QASessionManager, QA_STATES } from './qaStateMachine';

// Trailing silence (ms) before the VAD decides the user finished an answer.
// 2000ms was too aggressive — a normal thinking pause mid-answer tripped it and
// skipped the user before they were done. The final question uses a longer window
// because silence there COMPLETES the session and ends the recording (rather than
// just advancing), so a premature trigger loses the end of the last answer.
const SILENCE_DURATION_MS = 4000;
const SILENCE_DURATION_LAST_QUESTION_MS = 6000;

/**
 * useQASession - Custom hook for orchestrating Q&A Practice sessions
 *
 * Uses refs for audioContext and microphoneStream since these are created
 * AFTER recording starts, but VAD needs them later.
 */
const useQASession = ({
    audioContextRef,
    microphoneStreamRef,
    destinationNodeRef,
    playQuestionAudio,
    onSessionComplete
}) => {
    // State
    const [sessionState, setSessionState] = useState(QA_STATES.IDLE);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [progress, setProgress] = useState({ currentQuestion: 0, totalQuestions: 0, skippedCount: 0 });
    const [countdown, setCountdown] = useState(3);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState(null);
    const [sessionSummary, setSessionSummary] = useState(null);

    // Refs
    const sessionManagerRef = useRef(null);
    const vadRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const vadRetryTimeoutRef = useRef(null);

    /**
     * Initialize a new Q&A session with questions from backend
     */
    const initializeSession = useCallback((sessionData) => {
        console.log('useQASession: Initializing session', sessionData);

        if (!sessionData || !sessionData.questions || sessionData.questions.length === 0) {
            setError('No questions provided for session');
            return false;
        }

        // Create session manager with callbacks
        const manager = new QASessionManager(sessionData.questions, {
            onStateChange: (newState, oldState, state) => {
                console.log(`useQASession: State change ${oldState} -> ${newState}`);
                setSessionState(newState);

                setProgress({
                    currentQuestion: state.currentQuestionIndex + 1,
                    totalQuestions: state.totalQuestions,
                    skippedCount: state.answers.filter(a => a.skipped).length
                });
            },
            onQuestionStart: (question, progressData) => {
                console.log('useQASession: Question started', question);
                setCurrentQuestion(question);
            },
            onAwaitingAnswer: (question, progressData) => {
                console.log('useQASession: Awaiting answer for', question?.rephrasedQuestion);
                // On the last question, silence ends the whole recording, so give more grace.
                const isLastQuestion = progressData?.currentQuestion >= progressData?.totalQuestions;
                startVAD(0, isLastQuestion);
            },
            onAnswerRecorded: (answer, progressData) => {
                console.log('useQASession: Answer recorded', answer);
                setProgress(progressData);
            },
            onQuestionSkipped: (answer, progressData) => {
                console.log('useQASession: Question skipped', answer);
                // Stop the VAD — the safety timeout bypasses VAD's own silence detection,
                // so the VAD is still running and would block the next question's startVAD().
                if (vadRef.current) {
                    vadRef.current.stop();
                    vadRef.current = null;
                }
                setProgress(progressData);
            },
            onSessionComplete: (summary) => {
                console.log('useQASession: Session complete', summary);
                setSessionSummary(summary);
                cleanup();
                if (onSessionComplete) {
                    onSessionComplete(summary);
                }
            },
            onError: (err) => {
                console.error('useQASession: Error', err);
                setError(err);
            }
        });

        manager.setSessionId(sessionData.sessionId);
        sessionManagerRef.current = manager;
        setIsInitialized(true);
        setError(null);

        setProgress({
            currentQuestion: 1,
            totalQuestions: sessionData.questions.length,
            skippedCount: 0
        });

        return true;
    }, [onSessionComplete]);

    /**
     * Start the Q&A session (triggers countdown then first question)
     */
    const startSession = useCallback(() => {
        if (!sessionManagerRef.current || !isInitialized) {
            console.error('useQASession: Session not initialized');
            return;
        }

        console.log('useQASession: Starting session');

        setCountdown(3);
        countdownIntervalRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current);
                    countdownIntervalRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        sessionManagerRef.current.startSession(3);
    }, [isInitialized]);

    /**
     * Initialize and start Voice Activity Detection
     * Uses refs to get the CURRENT audioContext and microphoneStream.
     * Retries up to 3 times with delay if audio resources aren't ready yet.
     */
    const startVAD = useCallback((retryCount = 0, isLastQuestion = false) => {
        const ctx = audioContextRef?.current;
        const mic = microphoneStreamRef?.current;

        if (!ctx || !mic) {
            if (retryCount < 3) {
                console.warn(`useQASession: VAD resources not ready, retrying in 500ms (attempt ${retryCount + 1}/3)`,
                    { hasCtx: !!ctx, hasMic: !!mic });
                // Tracked so cleanup can cancel it. Left dangling, this fires after
                // unmount, clones the mic, and the clone is orphaned live: VAD.start()
                // stores the stream before createMediaStreamSource throws on the
                // already-closed AudioContext, and that throw is swallowed.
                vadRetryTimeoutRef.current = setTimeout(
                    () => startVAD(retryCount + 1, isLastQuestion), 500);
                return;
            }
            console.error('useQASession: Cannot start VAD after 3 retries - missing audioContext or microphoneStream',
                { hasCtx: !!ctx, hasMic: !!mic });
            // Surface it: with no VAD, nothing ever advances a question except
            // the 90s safety timeout, which records every answer as SKIPPED —
            // the whole session silently degrades to dead-air blocks.
            setError('Microphone monitoring failed to start. Please restart the session — your answers cannot be detected.');
            return;
        }

        // Always stop and discard any existing VAD before creating a fresh one.
        // This prevents "Already running" errors when a previous question's VAD
        // wasn't properly cleaned up (e.g., safety timeout skipped the question).
        if (vadRef.current) {
            vadRef.current.stop();
            vadRef.current = null;
        }

        vadRef.current = new VoiceActivityDetector(ctx, {
            silenceThreshold: -50,
            silenceDuration: isLastQuestion ? SILENCE_DURATION_LAST_QUESTION_MS : SILENCE_DURATION_MS,
            noSpeechTimeout: 30000,
            minSpeechDuration: 500
        });

        vadRef.current.setCallbacks({
            onSpeechStart: () => {
                console.log('useQASession: Speech started');
                if (sessionManagerRef.current) {
                    sessionManagerRef.current.notifySpeechDetected();
                }
            },
            onSpeechEnd: () => {
                console.log('useQASession: Speech ended');
            },
            onSilenceDetected: (data) => {
                console.log('useQASession: Silence detected', data);
                vadRef.current = null;
                if (sessionManagerRef.current) {
                    sessionManagerRef.current.notifySilenceDetected(data);
                }
            },
            onTimeout: (data) => {
                console.log('useQASession: VAD timeout', data);
                vadRef.current = null;
                if (sessionManagerRef.current) {
                    sessionManagerRef.current.notifyTimeout(data);
                }
            },
            onStateChange: (newState, oldState) => {
                console.log(`useQASession: VAD state ${oldState} -> ${newState}`);
            }
        });

        // Clone the mic stream so the VAD gets its own MediaStreamSource.
        // Creating two MediaStreamSource nodes from the same stream on the same
        // AudioContext causes the second to be silent in Chrome.
        vadRef.current.start(mic.clone());
        console.log('useQASession: VAD started');
    }, [audioContextRef, microphoneStreamRef]);

    const stopVAD = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.stop();
            console.log('useQASession: VAD stopped');
        }
    }, []);

    const pauseVAD = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.pause();
        }
    }, []);

    const resumeVAD = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.resume();
        }
    }, []);

    const notifyTTSStarted = useCallback(() => {
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifyTTSStarted();
        }
        pauseVAD();
    }, [pauseVAD]);

    const notifyTTSEnded = useCallback(() => {
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifyTTSEnded();
        }
    }, []);

    const nextQuestion = useCallback(() => {
        stopVAD();
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifySilenceDetected({ reason: 'manual_next' });
        }
    }, [stopVAD]);

    const skipQuestion = useCallback(() => {
        stopVAD();
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifyTimeout({ reason: 'manual_skip' });
        }
    }, [stopVAD]);

    const endSessionEarly = useCallback(() => {
        stopVAD();
        if (sessionManagerRef.current) {
            sessionManagerRef.current.completeSession();
        }
    }, [stopVAD]);

    const resetSession = useCallback(() => {
        cleanup();
        setSessionState(QA_STATES.IDLE);
        setCurrentQuestion(null);
        setProgress({ currentQuestion: 0, totalQuestions: 0, skippedCount: 0 });
        setCountdown(3);
        setIsInitialized(false);
        setError(null);
        setSessionSummary(null);
    }, []);

    const cleanup = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.stop();
            vadRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (vadRetryTimeoutRef.current) {
            clearTimeout(vadRetryTimeoutRef.current);
            vadRetryTimeoutRef.current = null;
        }
        if (sessionManagerRef.current) {
            sessionManagerRef.current.cleanup();
        }
    }, []);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    const getVADState = useCallback(() => {
        // ?? not ||: a legitimately falsy state value must not collapse to IDLE
        return vadRef.current?.getState() ?? VAD_STATES.IDLE;
    }, []);

    const getCurrentAudioLevel = useCallback(() => {
        // ?? not ||: a level of exactly 0 dBFS (clipping-loud) is falsy and
        // would read as -Infinity — an empty meter at maximum input.
        return vadRef.current?.getCurrentLevel() ?? -Infinity;
    }, []);

    return {
        sessionState,
        currentQuestion,
        progress,
        countdown,
        isInitialized,
        error,
        sessionSummary,

        initializeSession,
        startSession,
        notifyTTSStarted,
        notifyTTSEnded,
        nextQuestion,
        skipQuestion,
        endSessionEarly,
        resetSession,

        startVAD,
        stopVAD,
        pauseVAD,
        resumeVAD,
        getVADState,
        getCurrentAudioLevel
    };
};

export default useQASession;
